/**
 * The plugin catalogue, and what the machine says about each entry.
 *
 * A *plugin* here is a Revit or AutoCAD integration. The local agent is not one:
 * it ships inside the desktop app, the
 * user neither installs nor manages it, and listing it here would invite them to
 * try. Its status lives in the shell's footer instead.
 *
 * The interesting logic is the merge. Three sources describe one plugin and none
 * of them alone is the answer:
 *
 *   - the provisioner's `detect`, which knows what is on disk,
 *   - instance discovery, which knows what is running right now,
 *   - and this catalogue, which knows what exists to be installed at all.
 *
 * **The two halves of "installed" stay apart.** The provisioner reports the
 * plugin tree and host registration separately, deliberately: a tree with no
 * registration will never load, and a registration with no tree breaks host startup.
 * Collapsing them into one boolean hides both,
 * so `versionState` keeps four cases and the pane can say which one it is.
 */
import type {
  CadInstance,
  DetectedHost,
  DetectedVersion,
  DetectSnapshot,
  UpdatePlan,
} from "../bimgo";

export type { DetectedHost, DetectedVersion, DetectSnapshot };

export type PluginId = "revit" | "autocad";

export function pluginVendorsForBridge(bridgeVersion: number): readonly PluginId[] {
  return bridgeVersion >= 7 ? ["revit", "autocad"] : ["revit"];
}

export interface CatalogEntry {
  id: PluginId;
  /** The vendor it belongs to. Not shown on the page yet: every entry is one card. */
  group: "autodesk";
  name: string;
  hostName: "Revit" | "AutoCAD";
  publisher: string;
  /** i18n key for the one-line description. */
  descriptionKey: string;
  /**
   * False for a plugin that is announced but not shippable. The catalog shows
   * it behind a "launching soon" cover rather than hiding it — the point is to
   * say it is coming, while making it impossible to start.
   */
  available: boolean;
}

export const CATALOG: readonly CatalogEntry[] = [
  {
    id: "revit",
    group: "autodesk",
    name: "Autodesk Revit",
    hostName: "Revit",
    publisher: "Bimgo",
    descriptionKey: "plugins.catalog.revit.description",
    available: true,
  },
  {
    id: "autocad",
    group: "autodesk",
    name: "Autodesk AutoCAD",
    hostName: "AutoCAD",
    publisher: "Bimgo",
    descriptionKey: "plugins.catalog.autocad.description",
    available: true,
  },
];

/**
 * One version's state.
 *
 * `incomplete` and `orphaned` are the two half-installs: the first will not load,
 * while the second leaves a host registration pointing at missing files.
 */
export type VersionState = "ready" | "external" | "incomplete" | "orphaned" | "absent";

export function versionState(version: DetectedVersion): VersionState {
  if (version.installedState?.repairRequired) return "incomplete";
  // Checked first: a developer build is registered and loadable, so calling it `orphaned`
  // ("registered but missing files") would be wrong in the one situation where someone is
  // deliberately running a local build and needs the report to be true.
  if (version.activeExternal) return "external";
  // `orphaned` is reserved for a host registration that points at no plugin tree.
  const hasTree = version.treePresent;
  if (hasTree && version.addinPresent) return "ready";
  if (hasTree) return "incomplete";
  if (version.addinPresent) return "orphaned";
  return "absent";
}

/**
 * The kind of change between two plugin versions, and whether it can be applied live.
 *
 * A web-local copy of the desktop's `plugin-updates.updateTier` (the two sides do not share
 * code; kept in sync by name). Only a forward **patch** bump is command-set-only by team
 * convention, so only it can be hot-applied while the CAD host stays open,
 * then the plugin re-loads via `__reload_commands__`. Everything else — a minor/major that
 * could carry a pinned-assembly change, a downgrade, or a fresh install — needs
 * the whole-tree reinstall that requires the host to be closed. This only decides which UI path to
 * offer; the desktop re-decides authoritatively before it writes anything.
 */
export type UpdateTier = "patch" | "minor" | "major" | "same" | "downgrade";

export function updateTier(from: string | null, to: string): UpdateTier {
  const parts = (value: string) =>
    value.split("-")[0]!.split(".").map((n) => Number.parseInt(n, 10) || 0);
  if (!from) return "major";
  const a = parts(from);
  const b = parts(to);
  const aM = a[0] ?? 0, aN = a[1] ?? 0, aP = a[2] ?? 0;
  const bM = b[0] ?? 0, bN = b[1] ?? 0, bP = b[2] ?? 0;
  if (bM !== aM) return bM > aM ? "major" : "downgrade";
  if (bN !== aN) return bN > aN ? "minor" : "downgrade";
  if (bP !== aP) return bP > aP ? "patch" : "downgrade";
  return "same";
}

/** How a year's pending update should be applied: live patch, whole reinstall, or nothing. */
export function decideUpdate(installed: string | null, target: string): "patch" | "reinstall" | "none" {
  const tier = updateTier(installed, target);
  // `updateTier` compares the numbers only, so a release and a feature build of it
  // (`0.1.12` / `0.1.12-workflow-dlc`) read as "same". They are different trees: reinstall.
  if (tier === "same") return installed === target ? "none" : "reinstall";
  return tier === "patch" ? "patch" : "reinstall";
}

/**
 * Which of `years` have their CAD host open, so a whole-tree write can ask the user to close
 * it first (#189) instead of being refused — or worse, landing under a session that keeps
 * the old build loaded.
 *
 * A desktop that reports the open years is read per year: a 2024 session does not block a
 * 2025 install. One that predates the field only says whether any host is open, and then
 * every requested year counts — asking once too often beats a write the provisioner refuses.
 */
export function openHostYears(
  snapshot: Pick<DetectSnapshot, "hostRunning" | "revitRunning" | "runningVersions"> | undefined,
  years: readonly string[],
): string[] {
  if (!snapshot) return [];
  if (Array.isArray(snapshot.runningVersions)) {
    const open = new Set(snapshot.runningVersions);
    return years.filter((year) => open.has(year));
  }
  return snapshot.hostRunning ?? snapshot.revitRunning ? [...years] : [];
}

export interface PluginVersionView extends DetectedVersion {
  state: VersionState;
  /** Sessions of this product version that are running right now. */
  running: number;
  /**
   * The version an update would move this year to, or null when it is already there.
   *
   * Derived from the main process's plan rather than by comparing version strings here: the
   * precedence between the published pointer and the shipped tree lives in one place and
   * this is not it.
   */
  updateTo: string | null;
  /**
   * This plugin version publishes no build for this year.
   *
   * Distinct from "nothing is installed": the year is detected and the plugin simply does
   * not serve it, so there is no action to offer and "Not installed" reads as though there
   * were one. Only ever true for a year with nothing on disk — a year that *is* installed
   * keeps reporting what it has, whatever the current pointer covers.
   */
  unsupported: boolean;
}

export interface InstalledPluginView {
  id: PluginId;
  name: string;
  /** True when at least one installed year could move to a newer build. */
  updateAvailable: boolean;
  /** Newest year first — the one a user is most likely to act on. */
  versions: PluginVersionView[];
  /**
   * @deprecated Detected years an older desktop's provisioner kept out of {@link versions}
   * because they were outside its year table (#52). Desktops from #93 on send none: every
   * detected year is a row, and a year with no build says so in its own row. Rendered when
   * present so a hosted web meeting an older desktop keeps its sentence.
   */
  unsupportedVersions: string[];
  /** @deprecated See {@link unsupportedVersions}. */
  supportedVersions: string[];
  /**
   * The product years a plugin build exists for, newest first (#93) — the published pointer's
   * or the shipped tree's. What the dialog names beside a year that has none.
   */
  availableYears: string[];
  installedCount: number;
  runningCount: number;
  /** `attention` covers both half-installs; it is a state, not an error. */
  status: "running" | "installed" | "attention" | "not-installed";
}

/**
 * Newest first.
 *
 * A plain descending string compare, not a numeric one: supported product years are
 * four digits, so the two orders are identical, and the numeric version would be
 * an unfalsifiable branch — code no fixture can distinguish from this one. It
 * also keeps a hypothetical `2025.1` next to `2025` instead of coercing it to
 * `NaN`.
 */
function byYearDescending(a: DetectedVersion, b: DetectedVersion): number {
  return b.version.localeCompare(a.version);
}

/**
 * Running sessions, per product version.
 *
 * The manifest's generic host version identifies the product year. Anything
 * that does not match a detected year is still counted for the plugin as a
 * whole — a session we cannot attribute to a version is still a session, and
 * dropping it would show "not installed" beside a running host.
 */
function countByVersion(
  vendor: PluginId,
  instances: readonly CadInstance[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const instance of instances) {
    if (instance.host_kind !== vendor) continue;
    const version = instance.hostVersion ?? instance.revitVersion;
    if (!version) continue;
    counts.set(version, (counts.get(version) ?? 0) + 1);
  }
  return counts;
}

export function summarisePlugin(
  entry: CatalogEntry,
  host: DetectedHost | undefined,
  instances: readonly CadInstance[],
  plans: Record<string, UpdatePlan> = {},
  availableYears: readonly string[] = [],
): InstalledPluginView {
  const vendorInstances = instances.filter((instance) => instance.host_kind === entry.id);
  const running = countByVersion(entry.id, vendorInstances);
  const versions = [...(host?.versions ?? [])].sort(byYearDescending).map((version) => {
    const plan = plans[version.version];
    const state = versionState(version);
    return {
      ...version,
      state,
      running: running.get(version.version) ?? 0,
      unsupported:
        state === "absent" && plan?.action === "unavailable" && plan.cause === "no-artifact",
      // Only a plan that would actually move this year counts as an update. `current` and
      // `unavailable` both mean "nothing to offer", for opposite reasons — and a plan
      // naming the version already active is the same nothing, however it got there.
      updateTo: plan &&
        plan.action !== "unavailable" &&
        (plan.version !== version.installedPluginVersion ||
          state === "incomplete" ||
          state === "orphaned")
        ? plan.version
        : null,
    };
  });

  const installedCount = versions.filter((v) => v.state === "ready").length;
  // A developer build is a working plugin, so the year counts as usable — but not as
  // *installed*, because nothing of ours is on disk to update, remove or roll back.
  const externalCount = versions.filter((v) => v.state === "external").length;
  const updatable = versions.some((v) => v.state === "ready" && v.updateTo !== null);
  const attention = versions.some((v) => v.state === "incomplete" || v.state === "orphaned");
  const runningCount = vendorInstances.length;

  const status: InstalledPluginView["status"] = runningCount > 0
    ? "running"
    : installedCount + externalCount > 0
      ? "installed"
      : attention
        ? "attention"
        : "not-installed";

  return {
    id: entry.id,
    name: entry.name,
    updateAvailable: updatable,
    versions,
    // Same newest-first order as `versions`, from the same comparator, so a year is not
    // ordered one way in the list and another in the sentence below it.
    unsupportedVersions: [...(host?.unsupportedVersions ?? [])].sort((a, b) => b.localeCompare(a)),
    supportedVersions: [...(host?.supportedVersions ?? [])].sort((a, b) => b.localeCompare(a)),
    availableYears: [...availableYears].sort((a, b) => b.localeCompare(a)),
    installedCount,
    runningCount,
    status,
  };
}

/**
 * The pane's list: every catalogue entry that is *installed or running*.
 *
 * A plugin the user has never installed does not belong in "your plugins" — it
 * belongs in the catalog section below it.
 */
export function installedPlugins(
  detect: DetectSnapshot | Partial<Record<PluginId, DetectSnapshot>> | null,
  instances: readonly CadInstance[],
): InstalledPluginView[] {
  const views: InstalledPluginView[] = [];
  for (const entry of CATALOG) {
    const snapshot = detect && "hosts" in detect ? detect : detect?.[entry.id];
    const host = snapshot?.hosts.find((h) => h.vendor === entry.id);
    const view = summarisePlugin(entry, host, instances, snapshot?.plans ?? {}, snapshot?.availableYears ?? []);
    if (view.status !== "not-installed") views.push(view);
  }
  return views;
}

/**
 * One vendor's view, whether or not anything is installed yet.
 *
 * {@link installedPlugins} hides a vendor with nothing on disk — right for the pane's
 * "your plugins" list, wrong for the Manage dialog, which is exactly where a fresh
 * machine goes to install its first product version. This returns the same summary
 * unconditionally so the dialog always has version state, `updateTo` and running counts.
 */
export function pluginView(
  detect: DetectSnapshot | Partial<Record<PluginId, DetectSnapshot>> | null,
  instances: readonly CadInstance[],
  id: PluginId,
): InstalledPluginView | undefined {
  const entry = CATALOG.find((e) => e.id === id);
  if (!entry) return undefined;
  const snapshot = detect && "hosts" in detect ? detect : detect?.[id];
  const host = snapshot?.hosts.find((h) => h.vendor === id);
  return summarisePlugin(entry, host, instances, snapshot?.plans ?? {}, snapshot?.availableYears ?? []);
}

/**
 * Product years as one phrase: "2025-2027" when they run consecutively, else "2023, 2027".
 *
 * Ascending regardless of the order handed in — this reads as prose inside a sentence, not
 * as the newest-first list the rows use, and "2027-2025" reads as a typo.
 *
 * Three is the threshold for collapsing to a range because a two-year range is no shorter
 * than listing both and loses which two they are.
 */
export function formatYearRange(years: readonly string[]): string {
  const sorted = [...years].sort((a, b) => a.localeCompare(b));
  const numbers = sorted.map((year) => Number.parseInt(year, 10));
  const consecutive =
    numbers.length >= 3 &&
    numbers.every((year, i) => Number.isFinite(year) && (i === 0 || year === numbers[i - 1]! + 1));
  return consecutive ? `${sorted[0]}-${sorted[sorted.length - 1]}` : sorted.join(", ");
}

/**
 * Whether any build is known for this vendor: a year that is current, can be downloaded or
 * can be installed from the shipped tree. False only when no plan names a usable version —
 * no pointer could be read and the app carries none. Every year being up to date is not
 * "no build": that machine is simply current.
 */
export function hasKnownBuild(plans: Record<string, UpdatePlan> | undefined): boolean {
  return Object.values(plans ?? {}).some((plan) => plan.action !== "unavailable");
}

/** Years that can still be installed — a picker that offers a no-op is a trap. */
export function installableVersions(view: InstalledPluginView | undefined): string[] {
  return (view?.versions ?? []).filter((v) => v.state !== "ready").map((v) => v.version);
}

/**
 * Every detected version of one vendor, newest first.
 *
 * The install picker needs the full list — including years that are already
 * installed, shown and not selectable — while the pane's card needs the merged
 * view. Same order for both, from the same comparator, so a year is not in one
 * place on the card and another in the dialog.
 */
export function sortedVersions(
  detect: DetectSnapshot | null,
  vendor: PluginId,
): DetectedVersion[] {
  const host = detect?.hosts.find((h) => h.vendor === vendor);
  return [...(host?.versions ?? [])].sort(byYearDescending);
}
